import api from './api';

export interface AppointmentData {
  date: string;
  time: string;
  appointment_type: string;
  subject: string;
  message?: string;
}

export interface AppointmentResponse {
  success: boolean;
  appointment_id: number;
  message: string;
  appointment: {
    id: number;
    date: string;
    start_time: string;
    end_time: string;
    client_name: string;
    subject: string;
    appointment_type: string;
    status: string;
    created_at: string;
  };
}

export interface AdminAppointment {
  id: number;
  client_name: string;
  client_email: string;
  client_phone?: string;
  client_full_name: string;
  subject: string;
  message: string;
  appointment_type: string;
  appointment_date: string;
  appointment_time: string;
  slot_info?: {
    date: string;
    start_time: string;
    end_time: string;
  };
  status: string;
  status_display: string;
  internal_notes: string;
  created_at: string;
  updated_at: string;
}

export interface AppointmentsListResponse {
  appointments: AdminAppointment[];
  total_count: number;
}

class AppointmentApiService {
  /**
   * Crée un nouveau rendez-vous
   */
  async createAppointment(appointmentData: AppointmentData): Promise<AppointmentResponse> {
    try {
      console.log('🔄 Création du rendez-vous...', appointmentData);
      
      const response = await api.post('/api/appointments/create/', appointmentData);
      
      console.log('✅ Rendez-vous créé avec succès:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur lors de la création du rendez-vous:', error);
      
      // Extraire le message d'erreur
      let errorMessage = 'Erreur lors de la création du rendez-vous';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 401) {
        errorMessage = 'Vous devez être connecté pour prendre un rendez-vous';
      } else if (error.response?.status === 400) {
        errorMessage = 'Données invalides. Vérifiez vos informations.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Erreur serveur. Veuillez réessayer plus tard.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Formate une date pour l'affichage
   */
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  /**
   * Formate une heure pour l'affichage
   */
  formatTime(timeString: string): string {
    return timeString.substring(0, 5); // HH:MM
  }

  /**
   * Génère un message de confirmation
   */
  generateConfirmationMessage(appointment: AppointmentResponse['appointment']): string {
    const date = this.formatDate(appointment.date);
    const time = this.formatTime(appointment.start_time);
    const typeLabel = appointment.appointment_type === 'online' ? 'en ligne' : 'physique';
    
    return `Votre rendez-vous ${typeLabel} a été confirmé pour le ${date} à ${time}. Vous recevrez un email de confirmation avec les détails.`;
  }

  /**
   * Valide les données du rendez-vous
   */
  validateAppointmentData(data: AppointmentData): string[] {
    const errors: string[] = [];

    if (!data.date) {
      errors.push('La date est requise');
    }

    if (!data.time) {
      errors.push('L\'heure est requise');
    }

    if (!data.appointment_type) {
      errors.push('Le type de rendez-vous est requis');
    }

    if (!data.subject) {
      errors.push('Le sujet est requis');
    }

    // Vérifier que la date n'est pas dans le passé
    if (data.date) {
      const appointmentDate = new Date(data.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (appointmentDate < today) {
        errors.push('La date ne peut pas être dans le passé');
      }
    }

    return errors;
  }

  /**
   * Prépare les données pour l'envoi à l'API
   */
  prepareAppointmentData(formData: any): AppointmentData {
    return {
      date: formData.date,
      time: formData.time,
      appointment_type: formData.appointmentType,
      subject: formData.subject,
      message: formData.message || ''
    };
  }

  /**
   * Récupère tous les rendez-vous pour l'admin
   */
  async getAllAppointments(): Promise<AppointmentsListResponse> {
    try {
      console.log('🔄 Récupération de tous les rendez-vous...');
      
      const response = await api.get('/api/appointments/all/');
      
      console.log('✅ Rendez-vous récupérés avec succès:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur lors de la récupération des rendez-vous:', error);
      
      let errorMessage = 'Erreur lors de la récupération des rendez-vous';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.status === 401) {
        errorMessage = 'Vous devez être connecté';
      } else if (error.response?.status === 403) {
        errorMessage = 'Accès non autorisé. Droits administrateur requis.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Erreur serveur. Veuillez réessayer plus tard.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Met à jour le statut d'un rendez-vous
   */
  async updateAppointmentStatus(appointmentId: number, status: string, internalNotes?: string): Promise<AdminAppointment> {
    try {
      console.log(`🔄 Mise à jour du rendez-vous ${appointmentId}...`, { status, internalNotes });
      
      const response = await api.patch(`/api/appointments/${appointmentId}/update/`, {
        status,
        internal_notes: internalNotes
      });
      
      console.log('✅ Rendez-vous mis à jour avec succès:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur lors de la mise à jour du rendez-vous:', error);
      
      let errorMessage = 'Erreur lors de la mise à jour du rendez-vous';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.status === 401) {
        errorMessage = 'Vous devez être connecté';
      } else if (error.response?.status === 403) {
        errorMessage = 'Accès non autorisé. Droits administrateur requis.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Rendez-vous non trouvé';
      } else if (error.response?.status === 500) {
        errorMessage = 'Erreur serveur. Veuillez réessayer plus tard.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Formate la date et l'heure pour l'affichage
   */
  formatDateTime(date: string, time: string): string {
    const dateObj = new Date(`${date}T${time}`);
    return dateObj.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Retourne la couleur du badge selon le statut
   */
  getStatusColor(status: string): string {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }
}

export const appointmentApiService = new AppointmentApiService();
